import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import { Check as CheckIcon, Star as StarIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PricingPage = () => {
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Starter',
      price: '$49',
      period: '/month',
      description: 'Perfect for small businesses getting started',
      features: [
        'Up to 500 inventory items',
        '1 store location',
        'Basic inventory tracking',
        'Order management',
        'Email support',
        'Mobile app access',
        'Basic reporting',
      ],
      buttonText: 'Start Free Trial',
      buttonColor: 'primary',
      buttonVariant: 'outlined',
    },
    {
      name: 'Professional',
      price: '$99',
      period: '/month',
      description: 'Ideal for growing businesses with multiple needs',
      features: [
        'Up to 2,000 inventory items',
        'Up to 3 store locations',
        'Advanced inventory tracking',
        'Order & delivery management',
        'GPS delivery tracking',
        'CRM & marketing tools',
        'Priority email support',
        'Advanced reporting & analytics',
        'Image upload for items',
        'Low stock alerts',
      ],
      buttonText: 'Get Started',
      buttonColor: 'primary',
      buttonVariant: 'contained',
      popular: true,
    },
    {
      name: 'Enterprise',
      price: '$199',
      period: '/month',
      description: 'Complete solution for large enterprises',
      features: [
        'Unlimited inventory items',
        'Unlimited store locations',
        'Full inventory management suite',
        'Advanced delivery tracking',
        '3D warehouse visualization',
        'Complete CRM & marketing suite',
        'Real-time GPS tracking',
        'Custom integrations',
        'Phone & email support',
        'Custom reporting',
        'User management',
        'API access',
        'Priority feature requests',
      ],
      buttonText: 'Contact Sales',
      buttonColor: 'secondary',
      buttonVariant: 'contained',
    },
  ];

  const additionalFeatures = [
    'Free data migration from existing systems',
    'Comprehensive onboarding and training',
    '99.9% uptime guarantee',
    'GDPR and SOC 2 compliance',
    'Regular feature updates and improvements',
    'Mobile-optimized interface',
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #f5f5f5 0%, #e3f2fd 100%)',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              color: 'text.primary',
            }}
          >
            Simple, Transparent Pricing
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mb: 4,
              color: 'text.secondary',
              fontSize: { xs: '1.25rem', md: '1.5rem' },
            }}
          >
            Choose the perfect plan for your business. All plans include a 14-day free trial.
          </Typography>
        </Container>
      </Box>

      {/* Pricing Cards */}
      <Box sx={{ py: { xs: 4, md: 8 }, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {plans.map((plan, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 8,
                    },
                    border: plan.popular ? 2 : 1,
                    borderColor: plan.popular ? 'primary.main' : 'divider',
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="Most Popular"
                      color="primary"
                      icon={<StarIcon />}
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontWeight: 'bold',
                      }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1, p: 4 }}>
                    <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                      {plan.name}
                    </Typography>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Typography variant="h3" component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {plan.price}
                      </Typography>
                      <Typography variant="h6" component="span" sx={{ color: 'text.secondary' }}>
                        {plan.period}
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 3, color: 'text.secondary' }}>
                      {plan.description}
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <List dense>
                      {plan.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckIcon color="primary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature} 
                            primaryTypographyProps={{ fontSize: '0.9rem' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <Box sx={{ p: 4, pt: 0 }}>
                    <Button
                      variant={plan.buttonVariant}
                      color={plan.buttonColor}
                      size="large"
                      fullWidth
                      onClick={() => navigate('/login')}
                      sx={{
                        py: 2,
                        fontSize: '1rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {plan.buttonText}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Additional Features */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: 'grey.50' }}>
        <Container maxWidth="md">
          <Typography variant="h4" component="h2" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold', mb: 4 }}>
            What's Included in Every Plan
          </Typography>
          <Grid container spacing={2}>
            {additionalFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckIcon color="primary" sx={{ mr: 2 }} />
                  <Typography variant="body1">{feature}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Typography variant="h4" component="h2" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold', mb: 6 }}>
            Frequently Asked Questions
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Can I change plans at any time?
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and billing is prorated.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Is there a setup fee?
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                No setup fees. We also provide free data migration and onboarding support to get you started quickly.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                What happens to my data if I cancel?
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                You can export all your data at any time. We keep your data for 30 days after cancellation to allow for easy reactivation.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: 'primary.main',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Start your 14-day free trial today. No credit card required.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/login')}
            sx={{
              backgroundColor: 'white',
              color: 'primary.main',
              px: 4,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: 'grey.100',
              },
            }}
          >
            Start Free Trial
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default PricingPage;
